import React, { useContext, useEffect, useState, type FC, type SyntheticEvent, type PropsWithChildren } from 'react';

import { mqMin, BREAKPOINT } from '@lucid/theme';
import { isWindowAboveWidth, isBrowser, useSiteMetadata, getI18nData } from '@lucid/utils';
import styled from '@emotion/styled';

import type { Lucid } from '../../types/types';
import Box from '../box/box';
import Nav, { SUPER_NAV_HEIGHT, SITE_NAV_HEIGHT, type SharedNavOptions } from './nav';
import NavDropdown from './nav-dropdown';
import NavLink from './nav-link';
import NavMenuIcon from './nav-menu-icon';
import LanguageSwitcher from '../language-switcher/language-switcher';
import LayoutContext from '../context/layout-context';

export interface NavMenuOptions extends SharedNavOptions {
  /** The entity values to allow for customization of urls. */
  entityTranslations?: Partial<Lucid.Drupal.ExtendedDrupalNode>[];
  /** Checks if the Sign Up button should be hidden. */
  shouldHideSignUpButton?: boolean;
}

export interface NavMenuProps extends NavMenuOptions {
  /** The aria label for screen readers */
  buttonAriaLabel: string;
  /** The navigation buttons that appear at the end of the menu. */
  buttons: Lucid.Drupal.LinkInterface[];
  /** Allows the component to get additional styles. */
  className?: string;
  /** The Language Switcher Disabled Languages. */
  disabledLanguages?: Lucid.I18n.AvailableLanguages[];
  /** The entity values to allow for customization of urls. */
  entityTranslations?: Partial<Lucid.Drupal.ExtendedDrupalNode>[];
  /** The i18n translation data. */
  i18nData: Lucid.I18n.DataEdges;
  /** The navigation menu items to navigate across the site. */
  items: Lucid.Drupal.LinkInterface[];
  /** The navigation buttons to be used in SSR for intial render. */
  placeholderButtons?: Lucid.Drupal.LinkInterface[];
  /** The navigation links to be used in SSR for intial render. */
  placeholderLinks?: Lucid.Drupal.LinkInterface[];
}

const StyledUL = styled.ul<SharedNavOptions>(
  {
    display: 'flex',
    flexDirection: 'column',
    margin: 0,
    marginBlock: 0,
    paddingInline: 0,
    width: '100%',
    listStyle: 'none',
  },
  ({ theme, isSuperNav }) => [
    {
      padding: '1px 0 1px 1px',
    },
    isSuperNav && {
      '& > li > a:not([type=button])': {
        '&:hover': {
          color: theme.colors.blue[80],
        },
        '&:active, &:focus': {
          color: theme.colors.blue[80],
          backgroundColor: theme.colors.blue[5],
        },
        '&:focus, &:focus-visible': {
          backgroundColor: theme.colors.blue[5],
          outlineColor: theme.colors.blue[60],
        },
      },
    },
  ],
);

const NavMenu: FC<PropsWithChildren<NavMenuProps>> = ({
  buttonAriaLabel,
  buttons,
  className,
  disabledLanguages = ['da'],
  entityTranslations,
  isSuperNav = false,
  hasSuperNav = false,
  i18nData,
  items,
  placeholderButtons,
  placeholderLinks,
  shouldHideSignUpButton = false,
  ...rest
}) => {
  const {
    language: { shortLangPrefix, i18nKey },
  } = useContext(LayoutContext);
  const { product } = useSiteMetadata();
  const { isMenuOpen, setIsMenuOpen } = useContext(Nav.Context);
  const { setActiveMenuCallback } = useContext(Nav.SiteContext);
  const [navButtons, setNavButtons] = useState(placeholderButtons || placeholderLinks || []);
  const i18n = getI18nData(i18nData, i18nKey);
  const navMenuIconLabel = isSuperNav ? i18n('mktg-nav-mobile-toggle-suite') : i18n('mktg-nav-mobile-toggle');

  useEffect(() => {
    if (isBrowser()) {
      setNavButtons(buttons);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    if (shouldHideSignUpButton) {
      const currentPlaceholderButtons = navButtons.slice(0, 1);
      setNavButtons(currentPlaceholderButtons);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [shouldHideSignUpButton]);

  const handleOnClick = (e: SyntheticEvent) => {
    if (!isWindowAboveWidth(BREAKPOINT.TABLET_LANDSCAPE)) {
      e.preventDefault();
      if (!isMenuOpen) {
        setIsMenuOpen(true);
        setActiveMenuCallback([setIsMenuOpen]);
      } else {
        setIsMenuOpen(false);
      }
    }
  };

  return (
    <>
      <NavMenuIcon
        aria-label={navMenuIconLabel}
        isMenuOpen={isMenuOpen}
        isSuperNav={isSuperNav}
        onClick={handleOnClick}
      />
      <Box
        {...rest}
        css={theme => [
          {
            label: 'navMenuContainer',
            display: 'flex',
            flexDirection: 'column',
            zIndex: theme.zIndices.zNegative1,
            transform: 'translateY(-100%)',
            transition: 'z-index 0.5s step-start, transform 0.5s ease',
            position: 'absolute',
            top: 0,
            right: 0,
            left: 0,
            backgroundColor: theme.colors.white,
            height: `calc(100vh - (${SITE_NAV_HEIGHT} + ${hasSuperNav ? SUPER_NAV_HEIGHT : '0px'}))`,
            overflowY: 'scroll',
            [mqMin.tablet]: {
              paddingRight: '2.25rem',
              paddingLeft: '2.25rem',
            },
            [mqMin.tabletLandscape]: {
              zIndex: 'auto',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              height: 'unset',
              marginRight: 0,
              marginLeft: theme.space.s16,
              transform: 'unset',
              position: 'unset',
              overflowY: 'unset',
              paddingLeft: 'unset',
              paddingRight: 'unset',
            },
          },
          isSuperNav && {
            height: `calc(100vh - ${SUPER_NAV_HEIGHT})`,
            [mqMin.tabletLandscape]: {
              backgroundColor: theme.colors.coolGrey[5],
            },
          },
          isMenuOpen && {
            transform: `translateY(${isSuperNav ? SUPER_NAV_HEIGHT : SITE_NAV_HEIGHT})`,
            transition: 'z-index 0.5s step-end, transform 0.5s ease',
          },
        ]}
        // eslint-disable-next-line react/jsx-sort-props
        className={className}
      >
        <StyledUL
          css={{
            label: 'navMenuLinkUL',
            alignItems: 'center',
            [mqMin.tabletLandscape]: {
              height: '100%',
              flexDirection: 'row',
            },
          }}
          isSuperNav={isSuperNav}
        >
          {items.map((itemLink, index) => {
            const isDropdown = itemLink && !!itemLink?.links?.length;

            return (
              <li
                key={`navMenuLink-${itemLink.entityId}-${index + 1}`}
                css={theme => [
                  {
                    label: 'navMenuLinkLi',
                    width: '100%',
                    paddingRight: '1px',
                    '& > a': {
                      padding: `0 ${theme.space.s32}px`,
                    },
                    [mqMin.tabletLandscape]: {
                      paddingRight: 'unset',
                      height: '100%',
                      width: 'unset',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '& > a': {
                        padding: `0 ${theme.space.s8}px`,
                      },
                    },
                  },
                ]}
              >
                {isDropdown ? (
                  <NavDropdown isSuperNav={isSuperNav} link={itemLink} variant="navButton" />
                ) : (
                  <NavLink isSuperNav={isSuperNav} link={itemLink} product={product} variant="navButton" />
                )}
              </li>
            );
          })}
        </StyledUL>
        <StyledUL
          css={theme => ({
            label: 'navMenuButtonUL',
            alignItems: 'flex-end',
            '& > li:first-of-type': {
              '& > a:not([type=button])': {
                textAlign: 'center',
              },
            },
            '& > li:last-of-type, & > li:nth-last-of-type(2)': {
              width: '100%',
              [mqMin.tabletSmall]: {
                maxWidth: 'fit-content',
              },
              [mqMin.tabletLandscape]: {
                alignSelf: 'flex-start',
              },
            },
            '& > li > a:not([type=button])': {
              justifyContent: 'center',
            },
            padding: `0 ${theme.space.s32}px`,
            [mqMin.tabletSmall]: {
              flexDirection: 'row',
              justifyContent: 'flex-end',
            },
            [mqMin.tabletLandscape]: [
              {
                height: '100%',
                flexDirection: 'row',
                marginBlockStart: 0,
                marginBlockEnd: 0,
                paddingInlineStart: 0,
                justifyContent: 'flex-end',
                padding: 'unset',
                '& > li:first-of-type': {
                  '& > a:not([type=button])': {
                    textAlign: 'center',
                    maxWidth: theme.space.s104,
                  },
                },
              },
              isSuperNav && {
                '& > li:first-of-type': {
                  alignSelf: 'center',
                },
              },
            ],
            [mqMin.computer]: {
              '& > li:first-of-type': {
                '& > a:not([type=button])': {
                  textAlign: 'center',
                  maxWidth: 'unset',
                },
              },
            },
          })}
          isSuperNav={isSuperNav}
        >
          {isSuperNav && (
            <li key="navMenuLanguageSwitcherLi" css={{ height: 'inherit' }}>
              <LanguageSwitcher
                buttonAriaLabel={buttonAriaLabel}
                css={theme => ({
                  display: 'flex',
                  height: 'inherit',
                  order: 2,
                  alignSelf: 'center',
                  marginRight: 0,
                  padding: `${theme.space.s24}px 0`,
                  '& > button': {
                    paddingLeft: 0,
                    '&:focus, &:focus-visible': {
                      fontWeight: theme.fontWeights.slightlyStrong,
                      borderRadius: theme.radii.textOnly,
                      outline: 'none',
                      outlineColor: theme.colors.blue[60],
                    },
                    '&:active, &:focus, &:hover': {
                      color: theme.colors.blue[60],
                      fontWeight: theme.fontWeights.slightlyStrong,
                      borderRadius: theme.radii.textOnly,
                      textDecoration: 'none',
                      '& > span > svg > path': {
                        strokeWidth: '2px',
                        stroke: theme.colors.blue[60],
                      },
                      '& > span > svg > g > path': {
                        fill: theme.colors.blue[60],
                      },
                    },
                  },
                  [mqMin.tabletSmall]: {
                    order: 1,
                  },
                  [mqMin.computer]: {
                    padding: 'unset',
                  },
                })}
                disabledLanguages={disabledLanguages}
                entityTranslations={entityTranslations!}
                popupDirection="bottom"
                shortLangPrefix={shortLangPrefix}
              />
            </li>
          )}
          {navButtons.map((buttonLink, index) => (
            <li
              key={`navMenuButton-${buttonLink.entityId}-${index + 1}`}
              css={theme => [
                {
                  label: 'navMenuButtonLi',
                  order: 1,
                  width: '100%',
                  '&:first-of-type > a:not([type=button])': {
                    justifyContent: 'center',
                  },
                  [mqMin.tabletSmall]: {
                    order: 2,
                    paddingLeft: theme.space.s24,
                    alignSelf: 'center',
                    '&:first-of-type > a:not([type=button])': {
                      justifyContent: 'center',
                    },
                  },
                  [mqMin.tabletLandscape]: {
                    padding: `0 ${theme.space.s8}px 0 ${theme.space.s24}px`,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    width: 'unset',
                    justifyContent: 'flex-end',
                  },
                },
                isSuperNav && {
                  '& > a': {
                    '&:active, &:hover, &:focus, &:focus-visible': {
                      color: theme.colors.blue[60],
                    },
                    '&:focus, &:focus-visible': {
                      outlineColor: theme.colors.blue[60],
                    },
                  },
                },
              ]}
            >
              <NavLink
                displayAsButton={buttonLink.isButton}
                link={buttonLink}
                product={product}
                variant={buttonLink.isButton ? 'primary' : 'navButton'}
              />
            </li>
          ))}
        </StyledUL>
      </Box>
    </>
  );
};

export { NavMenu };
export default NavMenu;
